import { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Heading, SimpleGrid } from '@chakra-ui/react';
import { editRenewable } from '../../../../services/renewables';
import { enUSFormatExcludingTime, lenientParse } from '../../../../services/items';
import { DEFAULTFLATWEIGHT } from 'constants/renewables';
import { format } from 'date-fns';

import ComboboxFormikField from '../../../forms/ComboboxFormikField';
import DateFormikField from '../../../forms/DateFormikField';
import useItemCreationWizard from 'hooks/useItemCreationWizard';

const RenewableDefinitionPanel = ({ idx, definitions, type, formLabels, helpPageUrlItemTypeId = null }) => {
    const fieldNamePrefix = `definitions.${idx}`;

    const {
        itemTypeId: selectedItemTypeId,
        newItemId,
        availableWeatherZones,
        projectOnOffDates,
        updateAvailableWeatherZones,
    } = useItemCreationWizard();

    const updateDefinitions = useCallback(async () => {
        const subRenewablePayload = {
            renewableId: definitions.renewableDefinitionResponses[idx].id,
            description: definitions.renewableDefinitionResponses[idx].description,
            weatherZoneId: definitions.renewableDefinitionResponses[idx].weatherZoneId,
            moduleProcessId: definitions.renewableDefinitionResponses[idx].moduleProcessId,
            modelWeightId: DEFAULTFLATWEIGHT,
            plantTypeId: definitions.renewableDefinitionResponses[idx].plantTypeId,
        };
        var payload = {
            renewable: {
                description: definitions.renewableItem.description,
                bookId: definitions.renewableItem.bookId,
                itemTypeId: selectedItemTypeId,
                pricingPointId: definitions.renewableItem.pricingPointId,
                itemId: newItemId,
                longitude: definitions.renewableItem.longitude !== '' ? definitions.renewableItem.longitude : null,
                latitude: definitions.renewableItem.latitude !== '' ? definitions.renewableItem.latitude : null,
                securityId: definitions.renewableItem.securityId,
                rtelecBasisPriceId: definitions.renewableItem.rtelecBasisPriceId,
            },
            subRenewables: [{ ...subRenewablePayload }],
        };

        await editRenewable(payload);
    }, [idx, definitions, selectedItemTypeId, newItemId]);

    const moduleProcessIdChanged = async (value) => {
        definitions.renewableDefinitionResponses[idx].moduleProcessId = value;
        await updateDefinitions();
    };

    const weatherzoneIdChanged = async (value) => {
        definitions.renewableDefinitionResponses[idx].weatherZoneId = value;
        await updateDefinitions();
    };

    const plantTypeIdChanged = async (value) => {
        definitions.renewableDefinitionResponses[idx].plantTypeId = value;
        await updateDefinitions();
    };

    const onlineDateChanged = useCallback(
        async (value) => {
            definitions.renewableDefinitionResponses[idx].onlineDate = format(
                lenientParse(value),
                enUSFormatExcludingTime
            );
        },
        [idx, definitions]
    );

    const offlineDateChanged = useCallback(
        async (value) => {
            definitions.renewableDefinitionResponses[idx].offlineDate = format(
                lenientParse(value),
                enUSFormatExcludingTime
            );
        },
        [idx, definitions]
    );

    useEffect(() => {
        if (!Array.isArray(availableWeatherZones) || availableWeatherZones.length === 0) {
            updateAvailableWeatherZones();
        }
    }, [availableWeatherZones, updateAvailableWeatherZones, projectOnOffDates]);

    return (
        <>
            <Heading as="h4" variant="h4" textTransform="capitalize">
                <FormattedMessage id="renewable_definitions_heading" values={{ renewable: type }} />
            </Heading>
            <SimpleGrid columns={{ xl: 4 }} spacingX={6}>
                <ComboboxFormikField
                    id={`${fieldNamePrefix}.moduleProcessId`}
                    name={`${fieldNamePrefix}.moduleProcessId`}
                    mt={3}
                    label={formLabels.modelType}
                    options={definitions.modelTypes}
                    valueKey="id"
                    labelKey="labelName"
                    isFastField
                    validateOnBlur={false}
                    isRequired
                    onChange={moduleProcessIdChanged}
                    showPlaceholder={false}
                />

                <ComboboxFormikField
                    id={`${fieldNamePrefix}.weatherZoneId`}
                    name={`${fieldNamePrefix}.weatherZoneId`}
                    mt={3}
                    label={formLabels.weatherZone}
                    options={availableWeatherZones}
                    valueKey="id"
                    labelKey="name"
                    isFastField
                    validateOnBlur={false}
                    isRequired
                    onChange={weatherzoneIdChanged}
                    showPlaceholder={false}
                />

                <DateFormikField
                    id={`${fieldNamePrefix}.onlineDate`}
                    name={`${fieldNamePrefix}.onlineDate`}
                    label={formLabels.onlineDate}
                    isRequired={false}
                    showTimeInput={false}
                    isDisabled={false}
                    dateFormat={enUSFormatExcludingTime}
                    info={<FormattedMessage id="item_creation_wizard_renewables_online_date" />}
                    mt={3}
                    isInPortal={true}
                    onChange={onlineDateChanged}
                    placeholderText=""
                />

                <DateFormikField
                    id={`${fieldNamePrefix}.offlineDate`}
                    name={`${fieldNamePrefix}.offlineDate`}
                    label={formLabels.offlineDate}
                    isRequired={false}
                    showTimeInput={false}
                    isDisabled={false}
                    dateFormat={enUSFormatExcludingTime}
                    info={<FormattedMessage id="item_creation_wizard_renewables_offline_date" />}
                    mt={3}
                    isInPortal={true}
                    onChange={offlineDateChanged}
                    placeholderText=""
                />

                {Array.isArray(definitions?.plantTypes) && definitions?.plantTypes.length > 0 && (
                    <ComboboxFormikField
                        id={`${fieldNamePrefix}.plantTypeId`}
                        name={`${fieldNamePrefix}.plantTypeId`}
                        mt={3}
                        label={formLabels.plantType}
                        options={definitions?.plantTypes}
                        labelKey="description"
                        valueKey="id"
                        placeholderLabel="-"
                        placeholderValue={null}
                        showPlaceholder={true}
                        isFastField
                        validateOnBlur={false}
                        onChange={plantTypeIdChanged}
                    />
                )}

                <ComboboxFormikField
                    id={`${fieldNamePrefix}.modelWeightId`}
                    name={`${fieldNamePrefix}.modelWeightId`}
                    mt={3}
                    label={formLabels.dataWeight}
                    options={definitions.dataWeightings.filter((x) => x.id === DEFAULTFLATWEIGHT)}
                    labelKey="description"
                    valueKey="id"
                    placeholderValue=""
                    placeholderLabel={formLabels.selectPlaceholder}
                    isFastField
                    validateOnBlur={false}
                    isDisabled={true}
                />
            </SimpleGrid>
        </>
    );
};

export default RenewableDefinitionPanel;
